import React, { useState, useEffect } from 'react';
import {
    RiAddLine,
    RiDeleteBinLine,
    RiImageLine,
    RiLoader4Line,
    RiEditLine,
    RiCloseLine,
    RiArrowRightSLine,
    RiLeafLine,
    RiMapPinLine,
    RiBookLine,
    RiSearchLine
} from 'react-icons/ri';
import { motion, AnimatePresence } from 'framer-motion';
import { API_BASE_URL } from '../../config';

const DISTRIBUTION_GROUPS = [
    {
        region: "South Asia & Southeast Asia",
        countries: [
            "Bangladesh", "India", "Sri Lanka", "Myanmar", "Thailand", "Cambodia", "Laos", "Vietnam", "China Southeast", "Philippines", "Java", "Borneo", "Sumatra", "Sulawesi", "Maluku", "New Guinea", "Hainan"
        ]
    },
    {
        region: "North Africa & East Africa",
        countries: [
            "Algeria", "Morocco", "Tunisia", "Libya", "Egypt", "Sudan", "South Sudan", "Ethiopia", "Somalia", "Kenya", "Tanzania", "Uganda", "Rwanda", "Burundi"
        ]
    },
    {
        region: "Central & West Africa",
        countries: [
            "DR Congo", "Congo", "Gabon", "Cameroon", "Nigeria", "Niger", "Chad", "Central African Republic", "Mali", "Mauritania", "Senegal", "Gambia", "Guinea-Bissau", "Guinea", "Sierra Leone", "Liberia", "Ivory Coast", "Ghana", "Togo", "Benin", "Burkina"
        ]
    },
    {
        region: "Southern Africa",
        countries: [
            "Angola", "Zambia", "Malawi", "Mozambique", "Zimbabwe", "Botswana", "South Africa", "Namibia"
        ]
    },
    {
        region: "Americas",
        countries: [
            "USA", "Mexico", "Canada", "Brazil", "Colombia", "Venezuela", "Cuba", "Jamaica", "Haiti"
        ]
    },
    {
        region: "Europe & Asia-Pacific",
        countries: [
            "Russia", "China", "Japan", "Australia", "New Zealand"
        ]
    },
    {
        region: "Indian Ocean Islands",
        countries: [
            "Madagascar", "Mauritius", "Réunion"
        ]
    }
];

const ManageSpecies = () => {
    const [speciesList, setSpeciesList] = useState([]);
    const [genera, setGenera] = useState([]); // New
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(true);
    const [editingId, setEditingId] = useState(null);
    const [activeTab, setActiveTab] = useState('basic'); // basic, taxonomy, distribution, images

    const initialFormState = {
        category_id: 'Mammals',
        common_name: '',
        scientific_name: '',
        authorship: '',
        genus_id: '', // New
        family_id: '', // New
        family: '',
        description: '',
        images: [''],
        distribution: { native: [], introduced: [] },
        synonyms: [],
        classification: {
            kingdom: '',
            phylum: '',
            class: '',
            order: '',
            family: '',
            genus: '',
            species: ''
        },
        general_info: {}
    };

    const [formData, setFormData] = useState(initialFormState);
    const [customCategory, setCustomCategory] = useState('');
    const [isCustomCategory, setIsCustomCategory] = useState(false);

    useEffect(() => {
        fetchSpecies();
        fetchGenera(); // New
    }, []);

    const fetchGenera = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/genera/read.php`);
            const data = await response.json();
            if (response.ok) {
                setGenera(data.records || []);
            }
        } catch (error) {
            console.error('Error fetching genera:', error);
        }
    };

    const fetchSpecies = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/species/read.php`);
            const data = await response.json();
            if (response.ok) {
                setSpeciesList(data.records || []);
            }
        } catch (error) {
            console.error('Error fetching species:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const url = editingId
            ? `${API_BASE_URL}/species/update.php`
            : `${API_BASE_URL}/species/create.php`;

        const finalFormData = {
            ...formData,
            category_id: isCustomCategory ? customCategory : formData.category_id
        };

        const payload = editingId
            ? { ...finalFormData, id: editingId }
            : finalFormData;

        try {
            const response = await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            if (response.ok) {
                setFormData(initialFormState);
                setCustomCategory('');
                setIsCustomCategory(false);
                setEditingId(null);
                setActiveTab('basic');
                fetchSpecies();
            } else {
                alert(`Failed to ${editingId ? 'update' : 'create'} species`);
            }
        } catch (error) {
            console.error('Error saving species:', error);
        }
    };

    const handleEdit = (item) => {
        setEditingId(item.id);
        const standardCategories = [
            'Algae', 'Mosses', 'Ferns', 'Conifers', 'Flowering',
            'Insects', 'Molluscs', 'Annelids', 'Arthropods',
            'Echinoderms', 'Mammals', 'Birds', 'Reptiles',
            'Amphibians', 'Fish'
        ];
        const isStandard = standardCategories.includes(item.category_id);

        setIsCustomCategory(!isStandard);
        setCustomCategory(isStandard ? '' : item.category_id);

        setFormData({
            ...item,
            category_id: isStandard ? item.category_id : 'Other',
            genus_id: item.genus_id || '', // New
            family_id: item.family_id || '', // New
            images: item.images || [''],
            distribution: item.distribution || { native: [], introduced: [] },
            synonyms: item.synonyms || [],
            classification: item.classification || initialFormState.classification
        });
        setActiveTab('basic');
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const cancelEdit = () => {
        setEditingId(null);
        setFormData(initialFormState);
        setCustomCategory('');
        setIsCustomCategory(false);
        setActiveTab('basic');
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this species?')) return;

        try {
            const response = await fetch(`${API_BASE_URL}/species/delete.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id })
            });

            if (response.ok) {
                fetchSpecies();
            }
        } catch (error) {
            console.error('Error deleting species:', error);
        }
    };

    // Helper functions for dynamic arrays
    const addImageField = () => setFormData({ ...formData, images: [...formData.images, ''] });
    const removeImageField = (index) => {
        const newImages = formData.images.filter((_, i) => i !== index);
        setFormData({ ...formData, images: newImages });
    };

    const addSynonym = () => setFormData({
        ...formData,
        synonyms: [...formData.synonyms, { name: '', year: '', type: '' }]
    });

    const removeSynonym = (index) => {
        const newSynonyms = formData.synonyms.filter((_, i) => i !== index);
        setFormData({ ...formData, synonyms: newSynonyms });
    };

    const toggleCountry = (type, country) => {
        const currentCountries = formData.distribution[type] || [];
        const isSelected = currentCountries.includes(country);

        const newCountries = isSelected
            ? currentCountries.filter(c => c !== country)
            : [...currentCountries, country];

        setFormData({
            ...formData,
            distribution: {
                ...formData.distribution,
                [type]: newCountries
            }
        });
    };

    const filteredSpecies = speciesList.filter(item =>
        item.common_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.scientific_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.family?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.category_id?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="max-w-[1400px] mx-auto">
            <div className="flex justify-between items-center mb-10">
                <div>
                    <h2 className="text-4xl font-black text-slate-900 tracking-tight">Biological Audit</h2>
                    <p className="text-slate-500 font-medium mt-2">Manage the global biodiversity archives</p>
                </div>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-12 gap-10">
                {/* Manager Pane */}
                <div className="xl:col-span-12">
                    <div className="bg-white rounded-[2.5rem] shadow-[0_20px_50px_rgba(0,0,0,0.04)] border border-slate-100 overflow-hidden">
                        <div className="flex border-b border-slate-50">
                            {[
                                { id: 'basic', label: 'Identity', icon: RiLeafLine },
                                { id: 'taxonomy', label: 'Taxonomy', icon: RiBookLine },
                                { id: 'distribution', label: 'Global Range', icon: RiMapPinLine },
                                { id: 'images', label: 'Media Assets', icon: RiImageLine },
                            ].map(tab => (
                                <button
                                    key={tab.id}
                                    onClick={() => setActiveTab(tab.id)}
                                    className={`flex-1 py-6 flex items-center justify-center gap-3 text-xs font-black uppercase tracking-widest transition-all
                                        ${activeTab === tab.id ? 'bg-slate-900 text-white' : 'text-slate-400 hover:text-slate-600 hover:bg-slate-50'}`}
                                >
                                    <tab.icon size={18} />
                                    {tab.label}
                                </button>
                            ))}
                        </div>

                        <form onSubmit={handleSubmit} className="p-10">
                            <AnimatePresence mode="wait">
                                {activeTab === 'basic' && (
                                    <motion.div
                                        initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}
                                        className="grid grid-cols-1 md:grid-cols-2 gap-8"
                                    >
                                        <div className="space-y-6">
                                            <div>
                                                <label className="block text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 mb-2">Primary Classification</label>
                                                <select
                                                    value={isCustomCategory ? 'Other' : formData.category_id}
                                                    onChange={e => {
                                                        if (e.target.value === 'Other') {
                                                            setIsCustomCategory(true);
                                                        } else {
                                                            setIsCustomCategory(false);
                                                            setFormData({ ...formData, category_id: e.target.value });
                                                        }
                                                    }}
                                                    className="w-full p-4 bg-slate-50 rounded-2xl border-none text-sm font-bold focus:ring-2 focus:ring-emerald-500"
                                                >
                                                    <option value="Algae">Algae</option>
                                                    <option value="Mosses">Mosses</option>
                                                    <option value="Ferns">Ferns</option>
                                                    <option value="Conifers">Conifers</option>
                                                    <option value="Flowering">Flowering</option>
                                                    <option value="Insects">Insects</option>
                                                    <option value="Molluscs">Molluscs</option>
                                                    <option value="Annelids">Annelids</option>
                                                    <option value="Arthropods">Arthropods</option>
                                                    <option value="Echinoderms">Echinoderms</option>
                                                    <option value="Mammals">Mammals</option>
                                                    <option value="Birds">Birds</option>
                                                    <option value="Reptiles">Reptiles</option>
                                                    <option value="Amphibians">Amphibians</option>
                                                    <option value="Fish">Fish</option>
                                                    <option value="Other">Other (Custom category)...</option>
                                                </select>
                                                {isCustomCategory && (
                                                    <motion.div
                                                        initial={{ opacity: 0, y: -10 }}
                                                        animate={{ opacity: 1, y: 0 }}
                                                        className="mt-3"
                                                    >
                                                        <input
                                                            type="text"
                                                            placeholder="Enter new category name..."
                                                            value={customCategory}
                                                            onChange={(e) => setCustomCategory(e.target.value)}
                                                            className="w-full p-4 bg-slate-50 rounded-2xl border-2 border-emerald-100 text-sm font-bold focus:ring-2 focus:ring-emerald-500 outline-none"
                                                        />
                                                    </motion.div>
                                                )}
                                            </div>
                                            <div>
                                                <label className="block text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 mb-2">Common Appellation</label>
                                                <input
                                                    type="text"
                                                    value={formData.common_name}
                                                    onChange={e => setFormData({ ...formData, common_name: e.target.value })}
                                                    placeholder="e.g. Sri Lankan Leopard"
                                                    className="w-full p-4 bg-slate-50 rounded-2xl border-none text-sm font-bold focus:ring-2 focus:ring-emerald-500"
                                                    required
                                                />
                                            </div>
                                        </div>
                                        <div className="space-y-6">
                                            <div>
                                                <label className="block text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 mb-2">Scientific Binomial</label>
                                                <input
                                                    type="text"
                                                    value={formData.scientific_name}
                                                    onChange={e => setFormData({ ...formData, scientific_name: e.target.value })}
                                                    placeholder="e.g. Panthera pardus kotiya"
                                                    className="w-full p-4 bg-slate-50 rounded-2xl border-none text-sm font-bold italic focus:ring-2 focus:ring-emerald-500"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 mb-2">Taxonomic Genus</label>
                                                <select
                                                    value={formData.genus_id}
                                                    onChange={e => {
                                                        const selectedGenus = genera.find(g => g.id == e.target.value);
                                                        setFormData({
                                                            ...formData,
                                                            genus_id: e.target.value,
                                                            family_id: selectedGenus ? selectedGenus.family_id : formData.family_id,
                                                            family: selectedGenus ? selectedGenus.family_name : formData.family,
                                                            classification: {
                                                                ...formData.classification,
                                                                genus: selectedGenus ? selectedGenus.name : formData.classification.genus,
                                                                family: selectedGenus ? selectedGenus.family_name : formData.classification.family
                                                            }
                                                        });
                                                    }}
                                                    className="w-full p-4 bg-slate-50 rounded-2xl border-none text-sm font-bold focus:ring-2 focus:ring-emerald-500 italic"
                                                >
                                                    <option value="">Select Genus (Optional)...</option>
                                                    {genera.map(g => (
                                                        <option key={g.id} value={g.id}>{g.name} ({g.common_name})</option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div>
                                                <label className="block text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 mb-2">Biological Family</label>
                                                <input
                                                    type="text"
                                                    value={formData.family}
                                                    onChange={e => setFormData({ ...formData, family: e.target.value })}
                                                    placeholder="e.g. Felidae"
                                                    className="w-full p-4 bg-slate-50 rounded-2xl border-none text-sm font-bold focus:ring-2 focus:ring-emerald-500"
                                                />
                                            </div>
                                        </div>
                                        <div className="md:col-span-2">
                                            <label className="block text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 mb-2">Eco-Morphological Description</label>
                                            <textarea
                                                value={formData.description}
                                                onChange={e => setFormData({ ...formData, description: e.target.value })}
                                                className="w-full p-4 bg-slate-50 rounded-2xl border-none text-sm font-medium h-32 focus:ring-2 focus:ring-emerald-500 outline-none"
                                            />
                                        </div>
                                    </motion.div>
                                )}

                                {activeTab === 'taxonomy' && (
                                    <motion.div
                                        initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}
                                        className="space-y-8"
                                    >
                                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                            {Object.keys(formData.classification).map(rank => (
                                                <div key={rank}>
                                                    <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2">{rank}</label>
                                                    <input
                                                        type="text"
                                                        value={formData.classification[rank]}
                                                        onChange={e => setFormData({
                                                            ...formData,
                                                            classification: { ...formData.classification, [rank]: e.target.value }
                                                        })}
                                                        className="w-full p-3 bg-slate-50 rounded-xl border-none text-xs font-bold focus:ring-2 focus:ring-emerald-500"
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                        <div className="pt-8 border-t border-slate-50">
                                            <div className="flex justify-between items-center mb-6">
                                                <h4 className="text-sm font-black uppercase tracking-widest text-slate-900 leading-none">Known Synonyms</h4>
                                                <button type="button" onClick={addSynonym} className="text-emerald-600 text-[10px] font-bold uppercase tracking-widest flex items-center gap-1">
                                                    <RiAddLine /> Append Synonym
                                                </button>
                                            </div>
                                            <div className="space-y-4">
                                                {formData.synonyms.map((syn, idx) => (
                                                    <div key={idx} className="flex gap-4 items-end">
                                                        <div className="flex-1">
                                                            <input
                                                                placeholder="Scientific Name"
                                                                value={syn.name}
                                                                onChange={e => {
                                                                    const newSyns = [...formData.synonyms];
                                                                    newSyns[idx].name = e.target.value;
                                                                    setFormData({ ...formData, synonyms: newSyns });
                                                                }}
                                                                className="w-full p-3 bg-slate-50 rounded-xl border-none text-xs font-bold"
                                                                required
                                                            />
                                                        </div>
                                                        <div className="w-24">
                                                            <input
                                                                placeholder="Year"
                                                                value={syn.year}
                                                                onChange={e => {
                                                                    const newSyns = [...formData.synonyms];
                                                                    newSyns[idx].year = e.target.value;
                                                                    setFormData({ ...formData, synonyms: newSyns });
                                                                }}
                                                                className="w-full p-3 bg-slate-50 rounded-xl border-none text-xs font-bold"
                                                            />
                                                        </div>
                                                        <button type="button" onClick={() => removeSynonym(idx)} className="p-3 text-slate-300 hover:text-red-500">
                                                            <RiDeleteBinLine size={18} />
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </motion.div>
                                )}

                                {activeTab === 'distribution' && (
                                    <motion.div
                                        initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}
                                        className="space-y-10"
                                    >
                                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
                                            {/* Native Range Selection */}
                                            <div className="bg-emerald-50/20 p-8 rounded-[2rem] border border-emerald-100/50">
                                                <div className="flex items-center gap-3 mb-6">
                                                    <div className="w-10 h-10 rounded-full bg-emerald-600 flex items-center justify-center text-white font-bold text-xs ring-4 ring-emerald-100">N</div>
                                                    <div>
                                                        <h4 className="text-sm font-black uppercase tracking-widest text-slate-900">Native Bio-Range</h4>
                                                        <p className="text-[10px] text-emerald-600 font-bold uppercase tracking-wider">Historical & Indigenous territory</p>
                                                    </div>
                                                </div>

                                                <div className="space-y-8 max-h-[500px] overflow-y-auto pr-4 scrollbar-thin scrollbar-thumb-emerald-200">
                                                    {DISTRIBUTION_GROUPS.map(group => (
                                                        <div key={group.region}>
                                                            <h5 className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 mb-4 border-b border-emerald-100 pb-2">{group.region}</h5>
                                                            <div className="grid grid-cols-2 gap-2">
                                                                {group.countries.map(country => (
                                                                    <button
                                                                        key={country}
                                                                        type="button"
                                                                        onClick={() => toggleCountry('native', country)}
                                                                        className={`text-left px-3 py-2 rounded-xl text-xs font-bold transition-all border ${formData.distribution.native.includes(country)
                                                                            ? 'bg-emerald-600 text-white border-emerald-600 shadow-lg shadow-emerald-900/10'
                                                                            : 'bg-white text-slate-500 border-slate-100 hover:border-emerald-200'
                                                                            }`}
                                                                    >
                                                                        {country}
                                                                    </button>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>

                                            {/* Introduced Range Selection */}
                                            <div className="bg-amber-50/20 p-8 rounded-[2rem] border border-amber-100/50">
                                                <div className="flex items-center gap-3 mb-6">
                                                    <div className="w-10 h-10 rounded-full bg-amber-500 flex items-center justify-center text-white font-bold text-xs ring-4 ring-amber-100">I</div>
                                                    <div>
                                                        <h4 className="text-sm font-black uppercase tracking-widest text-slate-900">Introduced Range</h4>
                                                        <p className="text-[10px] text-amber-600 font-bold uppercase tracking-wider">Naturalized or anthropogenic habitats</p>
                                                    </div>
                                                </div>

                                                <div className="space-y-8 max-h-[500px] overflow-y-auto pr-4 scrollbar-thin scrollbar-thumb-amber-200">
                                                    {DISTRIBUTION_GROUPS.map(group => (
                                                        <div key={group.region}>
                                                            <h5 className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 mb-4 border-b border-amber-100 pb-2">{group.region}</h5>
                                                            <div className="grid grid-cols-2 gap-2">
                                                                {group.countries.map(country => (
                                                                    <button
                                                                        key={country}
                                                                        type="button"
                                                                        onClick={() => toggleCountry('introduced', country)}
                                                                        className={`text-left px-3 py-2 rounded-xl text-xs font-bold transition-all border ${formData.distribution.introduced.includes(country)
                                                                            ? 'bg-amber-500 text-white border-amber-500 shadow-lg shadow-amber-900/10'
                                                                            : 'bg-white text-slate-500 border-slate-100 hover:border-amber-200'
                                                                            }`}
                                                                    >
                                                                        {country}
                                                                    </button>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    </motion.div>
                                )}

                                {activeTab === 'images' && (
                                    <motion.div
                                        initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}
                                        className="space-y-6"
                                    >
                                        {formData.images.map((url, idx) => (
                                            <div key={idx} className="flex gap-4 items-center">
                                                <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center shrink-0 overflow-hidden">
                                                    {url ? <img src={url} alt="" className="w-full h-full object-cover" /> : <RiImageLine className="text-slate-300" />}
                                                </div>
                                                <input
                                                    type="url"
                                                    value={url}
                                                    onChange={e => {
                                                        const newImgs = [...formData.images];
                                                        newImgs[idx] = e.target.value;
                                                        setFormData({ ...formData, images: newImgs });
                                                    }}
                                                    placeholder="Image URL (Unsplash or direct link)"
                                                    className="flex-1 p-4 bg-slate-50 rounded-2xl border-none font-bold text-sm focus:ring-2 focus:ring-emerald-500"
                                                />
                                                <button type="button" onClick={() => removeImageField(idx)} className="p-2 text-slate-300 hover:text-red-500">
                                                    <RiDeleteBinLine size={20} />
                                                </button>
                                            </div>
                                        ))}
                                        <button type="button" onClick={addImageField} className="flex items-center gap-2 text-emerald-600 font-bold text-xs uppercase tracking-widest pl-4">
                                            <RiAddLine /> Add Image URL
                                        </button>
                                    </motion.div>
                                )}
                            </AnimatePresence>

                            <div className="mt-12 pt-10 border-t border-slate-50 flex gap-4">
                                <button className={`flex-1 font-black py-5 rounded-2xl text-xs uppercase tracking-[0.2em] transition-all shadow-xl active:scale-95 ${editingId ? 'bg-amber-500 hover:bg-amber-600 text-white shadow-amber-900/10' : 'bg-emerald-600 hover:bg-emerald-700 text-white shadow-emerald-900/10'
                                    }`}>
                                    {editingId ? 'Push Synchronization' : 'Commit New Specimen'}
                                </button>
                                {editingId && (
                                    <button type="button" onClick={cancelEdit} className="px-8 bg-slate-100 text-slate-400 font-black rounded-2xl hover:bg-slate-200 transition-colors">
                                        <RiCloseLine size={24} />
                                    </button>
                                )}
                            </div>
                        </form>
                    </div>
                </div>

                {/* Audit List Pane */}
                <div className="xl:col-span-12 mt-10">
                    <div className="flex items-center justify-between mb-8">
                        <h3 className="text-xl font-bold text-slate-900">Archived Specimens</h3>
                        <div className="relative group">
                            <RiSearchLine className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-emerald-600 transition-colors" />
                            <input
                                type="text"
                                placeholder="Search archives..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pl-11 pr-4 py-3 bg-white border border-slate-100 rounded-full text-sm font-medium focus:ring-2 focus:ring-emerald-500 outline-none w-64 shadow-sm transition-all"
                            />
                        </div>
                    </div>

                    {loading ? (
                        <div className="flex justify-center py-20 text-emerald-500">
                            <RiLoader4Line className="animate-spin text-4xl" />
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {filteredSpecies.map((item) => (
                                <div key={item.id} className="bg-white p-5 rounded-[2rem] border border-slate-100 shadow-sm flex flex-col group hover:shadow-xl transition-all duration-500">
                                    <div className="h-48 bg-slate-100 rounded-[1.5rem] mb-6 overflow-hidden relative">
                                        {item.images && item.images[0] ? (
                                            <img src={item.images[0]} alt={item.common_name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-1000" />
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center text-slate-300">
                                                <RiImageLine size={32} />
                                            </div>
                                        )}
                                        <div className="absolute top-4 left-4">
                                            <span className="bg-white/95 backdrop-blur-md px-3 py-1 text-[8px] font-black text-slate-900 uppercase tracking-widest rounded-full border border-slate-100">
                                                {item.category_id}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="px-2">
                                        <h4 className="font-bold text-slate-900 text-lg mb-1">{item.common_name}</h4>
                                        <p className="text-slate-400 text-sm italic font-serif mb-4 leading-none">{item.scientific_name}</p>

                                        <div className="flex items-center justify-between pt-4 border-t border-slate-50">
                                            <div className="flex gap-1">
                                                <button onClick={() => handleEdit(item)} className="p-2 text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-all">
                                                    <RiEditLine size={18} />
                                                </button>
                                                <button onClick={() => handleDelete(item.id)} className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all">
                                                    <RiDeleteBinLine size={18} />
                                                </button>
                                            </div>
                                            <RiArrowRightSLine className="text-slate-200 group-hover:text-emerald-500 group-hover:translate-x-1 transition-all" size={20} />
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ManageSpecies;
